import { useEffect, useState } from 'preact/hooks';
import { fetchPublicAsset } from '../utils/publicFetch';

export type I18nRuntime = {
  getLocale: () => string;
  setLocale: (locale: string) => void;
  subscribe: (fn: () => void) => () => void;
  t: (key: string, fallback?: string) => string;
  isLoaded: () => boolean;
  loadLocale: (locale: string, opts?: { force?: boolean }) => Promise<boolean>;
};

type Dict = Record<string, Record<string, string>>;

// Dynamic translations loaded from JSON files
let loadedDict: Dict = {};
let loadedLocales: Set<string> = new Set();
let availableLocales: Set<string> | null = null;
let availableLocalesPromise: Promise<Set<string>> | null = null;

function normalizeLocale(raw: string | null | undefined): string {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return 'en';
  // Normalize "pt-BR"/"ru-RU" -> "pt"/"ru".
  const base = v.split(/[-_]/)[0];
  const clean = base.replace(/[^a-z]/g, '');
  // Telegram may send 3-letter codes like "fil". We only support 2-letter locales in /locales/ui.xx.json.
  if (!/^[a-z]{2}$/.test(clean)) return 'en';
  return clean;
}

function detectLocaleFromTelegram(): string | null {
  const tg = (window as any).Telegram?.WebApp;
  const lang = tg?.initDataUnsafe?.user?.language_code;
  if (typeof lang === 'string' && lang) return normalizeLocale(lang);
  return null;
}

async function loadAvailableLocales(): Promise<Set<string>> {
  if (availableLocales) return availableLocales;
  if (availableLocalesPromise) return availableLocalesPromise;
  availableLocalesPromise = (async () => {
    // Best-effort: if manifest is missing/unreachable, assume only EN exists.
    const out = new Set<string>(['en']);
    try {
      const r = await fetchPublicAsset(`/locales/manifest.json?t=${Date.now()}`, undefined, { expected_content_type: 'json', stage: 'locales_manifest' });
      if (r.ok) {
        const j: any = await r.json().catch(() => ({}));
        const list0 = Array.isArray(j?.locales)
          ? j.locales
          : (Array.isArray(j?.namespaces?.ui) ? j.namespaces.ui : []);
        for (const x of (list0 || [])) {
          const loc = normalizeLocale(String(x || ''));
          if (loc) out.add(loc);
        }
      }
    } catch {}
    availableLocales = out;
    return out;
  })();
  return await availableLocalesPromise;
}

function pickSupportedLocale(requested: string, avail: Set<string>): string {
  const loc = normalizeLocale(requested);
  if (loc && avail.has(loc)) return loc;
  return 'en';
}

async function fetchLocaleData(locale: string): Promise<{ ok: boolean; data: Record<string, string> }> {
  try {
    // 1) Base UI locale file (namespace-based)
    const response = await fetchPublicAsset(`/locales/ui.${locale}.json?t=${Date.now()}`, undefined, { expected_content_type: 'json' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    // 2) Commands namespace (static JSON)
    let cmdData: any = {};
    try {
      const apiResp = await fetchPublicAsset(`/locales/commands.${locale}.json?t=${Date.now()}`, undefined, { expected_content_type: 'json' });
      if (apiResp.ok) cmdData = await apiResp.json();
    } catch {
      cmdData = {};
    }

    // 3) Tasks namespace (static JSON)
    // (content translations: task.<id>.title / task.<id>.description)
    let tasksData: any = {};
    try {
      const r = await fetchPublicAsset(`/locales/tasks.${locale}.json?t=${Date.now()}`, undefined, { expected_content_type: 'json' });
      if (r.ok) tasksData = await r.json();
    } catch {
      tasksData = {};
    }

    // 4) Quests namespace (static JSON)
    // (quest translations: quest.template.<id>, quest.objective.<id>, quest.daily_title, etc.)
    let questsData: any = {};
    try {
      const r = await fetchPublicAsset(`/locales/quests.${locale}.json?t=${Date.now()}`, undefined, { expected_content_type: 'json' });
      if (r.ok) questsData = await r.json();
    } catch {
      questsData = {};
    }

    // Filter out _meta key if present
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries({ ...(data || {}), ...(cmdData || {}), ...(tasksData || {}), ...(questsData || {}) })) {
      if (key !== '_meta' && typeof value === 'string') {
        result[key] = value;
      }
    }
    // Consider locale usable only if UI namespace returned something.
    const ok = Object.keys(data || {}).length > 0;
    return { ok, data: result };
  } catch (err) {
    console.warn(`Failed to load locale ${locale}:`, err);
    return { ok: false, data: {} };
  }
}

export function createI18nRuntime(): I18nRuntime {
  const listeners = new Set<() => void>();
  let isReady = false;

  function readInitialLocale(): string {
    try {
      const saved = localStorage.getItem('i18n_locale');
      if (saved) return normalizeLocale(saved);
    } catch {}
    const tgLocale = detectLocaleFromTelegram();
    return tgLocale || 'en';
  }

  // Keep a requested locale, but we will resolve to a supported one (via manifest) before fetching files.
  let locale = 'en';
  let requestedLocale = readInitialLocale();

  function notify() {
    listeners.forEach((fn) => {
      try { fn(); } catch {}
    });
  }

  async function loadLocale(loc: string, opts: { force?: boolean } = {}): Promise<boolean> {
    const avail = await loadAvailableLocales();
    const normalized = pickSupportedLocale(loc, avail);
    if (!opts.force && loadedLocales.has(normalized)) return true;
    
    const r = await fetchLocaleData(normalized);
    // Even if data is empty, mark locale as loaded to avoid re-fetch loops.
    loadedDict[normalized] = r?.data || {};
    loadedLocales.add(normalized);
    notify();
    return Boolean(r?.ok);
  }

  function setLocale(next: string) {
    requestedLocale = String(next || '');
    const n = normalizeLocale(next);
    // If manifest is already loaded, resolve immediately (so we don't request missing /locales/ui.xx.json).
    const resolved = (availableLocales && n) ? (availableLocales.has(n) ? n : 'en') : n;
    if (resolved === locale) return;
    locale = resolved || 'en';
    try { localStorage.setItem('i18n_locale', locale); } catch {}
    
    // Optimistic update; will fallback to EN if this locale is not available.
    notify();
    if (!loadedLocales.has(locale)) {
      loadLocale(locale).then((ok) => {
        if (!ok && locale !== 'en' && locale === normalizeLocale(requestedLocale)) {
          locale = 'en';
          try { localStorage.setItem('i18n_locale', locale); } catch {}
        }
        notify();
      });
    }
  }

  function t(key: string, fallback?: string): string {
    const k = String(key || '').trim();
    if (!k) return '';

    // Try loaded translations first
    const loadedTable = loadedDict[locale];
    if (loadedTable?.[k]) return loadedTable[k];

    // Fallback to loaded EN
    const loadedEn = loadedDict['en'];
    if (loadedEn?.[k]) return loadedEn[k];

    return (typeof fallback === 'string' && fallback) ? fallback : k;
  }

  // Initial load: fetch current locale and English as fallback
  (async () => {
    const avail = await loadAvailableLocales();
    await loadLocale('en');
    const desired = pickSupportedLocale(requestedLocale, avail);
    locale = desired;
    try { localStorage.setItem('i18n_locale', locale); } catch {}
    if (locale !== 'en') {
      const ok = await loadLocale(locale);
      if (!ok) locale = 'en';
    }
    isReady = true;
    notify();
  })();

  return {
    getLocale: () => locale,
    setLocale,
    subscribe: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    t,
    isLoaded: () => isReady,
    loadLocale,
  };
}

export function useI18n(i18n: I18nRuntime) {
  const [locale, setLocale] = useState(i18n.getLocale());
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const unsub = i18n.subscribe(() => {
      setLocale(i18n.getLocale());
      forceUpdate(n => n + 1); // Force re-render when translations load
    });
    return () => unsub();
  }, [i18n]);

  return { locale, t: i18n.t, setLocale: i18n.setLocale };
}
